import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentProps, forwardRef } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { cn } from '../utils/className';

const inputVariants = cva(
  [
    'px-2',
    'py-1.5',
    'min-h-11', // Ensure minimum 44px height on mobile
    'md:min-h-0', // Remove min-height on desktop
    'rounded',
    'border',
    'size-p2',
    'font-regular',
    'transition-colors',
    'outline-none',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-surface-primary',
          'border-border-primary',
          'text-primary',
          'placeholder:text-disabled',
          'hover:bg-blue-050',
          'hover:border-border-focus',
          'focus:border-border-focus',
          'focus:bg-surface-primary',
          'focus:text-focus',
        ],
        inactive: [
          'bg-surface-primary',
          'border-border-primary',
          'text-secondary',
          'placeholder:text-tertiary',
          'hover:bg-blue-050',
          'hover:border-border-focus',
          'focus:border-border-focus',
          'focus:bg-surface-primary',
          'focus:text-focus',
        ],
        error: [
          'bg-surface-negative-support',
          'border-border-negative',
          'text-negative',
          'placeholder:text-negative',
          'focus:border-border-negative',
        ],
        disabled: [
          'bg-surface-primary-support',
          'border-border-disabled',
          'text-disabled',
          'placeholder:text-disabled',
          'cursor-not-allowed',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface InputProps
  extends ComponentProps<'input'>, VariantProps<typeof inputVariants> {
  label?: string;
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, error, variant, className, disabled, autoFocus, ...props },
    ref
  ) => {
    const { isMobileDevice } = useResponsive();

    // Determine the variant based on props
    const effectiveVariant = disabled ? 'disabled' : error ? 'error' : variant;

    return (
      <div className='flex flex-col gap-1.5'>
        {label && (
          <label className='size-p2 font-regular text-primary'>{label}</label>
        )}
        <input
          ref={ref}
          disabled={disabled}
          autoFocus={isMobileDevice ? false : autoFocus}
          className={cn(
            inputVariants({ variant: effectiveVariant }),
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

Input.displayName = 'Input';
